import { createStoreLogger } from "../helpers/loggers";
import type { AxiosResponse } from "axios";

export type NormalizedError = Error & { status?: number; details?: unknown; code?: string };

export const normalizeError = (raw: any): NormalizedError => {
  const e = new Error(
    raw?.response?.data?.message || raw?.message || "Request failed"
  ) as NormalizedError;
  e.status = raw?.response?.status;
  e.details = raw?.response?.data;
  e.code = raw?.code;
  return e;
};

// Type helper: unwrap AxiosResponse promise signatures (like in BaseStore)
export type UnwrapResponseFn<F> = F extends (...args: infer A) => Promise<infer R>
  ? R extends AxiosResponse<infer D>
    ? (...args: A) => Promise<D>
    : F
  : F;

export type WrappedApiClient<T> = {
  [K in keyof T]: UnwrapResponseFn<T[K]>;
};

/**
 * createProxiedClient wraps every async method of an auto-generated API client so that:
 * - AxiosResponse is unwrapped and only `data` is returned
 * - Errors are normalized & logged
 * - Types are transformed so callers see the unwrapped data shape
 */
export const createProxiedClient = <T extends object>(client: T): WrappedApiClient<T> => {
  const logger = createStoreLogger((client as any).constructor.name);

  const handler: ProxyHandler<T> = {
    get: (target, prop: string | symbol) => {
      const orig = (target as any)[prop];
      if (typeof orig !== "function") return orig;
      return async (...args: any[]) => {
        const action = String(prop);
        const start = performance.now();
        try {
          const res = await orig.apply(target, args);
          const data = (res as any)?.data ?? res; // Support already unwrapped responses
          const ms = (performance.now() - start).toFixed(2);
          // logger.debug(`✓ ${action}() [${ms}ms]`, { args, data });
          return data;
        } catch (raw) {
          const err = normalizeError(raw);
          if (err.code !== "ERR_CANCELED") {
            logger.error(`✗ ${action}()`, { args, err });
          }
          throw err;
        }
      };
    },
  };
  return new Proxy(client, handler) as WrappedApiClient<T>;
};
